<template>
    <markdown
        v-if="outputOnly"
        ref="outputOnlyContainer"
        id="output-only-container"
        :class="`editor-output ${outputClass || ''}`"
        :style="outputStyle"
        :source="modelValue"
        breaks
        linkify
    />
    <div class="editor" :class="{fullscreen:fullscreen}" v-else>
        <q-card
            :flat="$q.dark.isActive"
            :square="$q.dark.isActive"
            :bordered="$q.dark.isActive"
        >
            <q-toolbar>
                <q-btn :dense="dense" flat icon="mdi-link" @click="wrapText('[', '](http://)', true)" >
                    <q-tooltip>Link</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-format-bold" @click="wrapText('**', '**')" >
                    <q-tooltip>Bold</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-format-strikethrough" @click="wrapText('~~', '~~')" >
                    <q-tooltip>Strike</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-format-italic" @click="wrapText('_', '_')" >
                    <q-tooltip>Italic</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-format-subscript" @click="wrapText('~', '~')" >
                    <q-tooltip>Sub</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-format-superscript" @click="wrapText('^', '^')" >
                    <q-tooltip>Sup</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-format-header-pound" @click="prependText('#')" >
                    <q-tooltip>Heading</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-format-quote-close" @click="prependText('>')" >
                    <q-tooltip>Quote</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat @click="wrapText('==', '==')" >
                    <q-badge rounded color="yellow" />
                    <q-icon name="mdi-format-color-highlight" />
                    <q-tooltip>Highlight</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-image" @click="wrapText('![', '](IMAGE_URL)', true)" >
                    <q-tooltip>Add Image</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-help" @click="openInTab('https://www.markdownguide.org/cheat-sheet/')" >
                    <q-tooltip>Help</q-tooltip>
                </q-btn>

                <q-space />
                
                <q-btn :dense="dense" flat icon="mdi-palette-swatch" :color="style ? 'positive' : undefined" @click="style = !style" >
                    <q-tooltip>CSS & Style</q-tooltip>
                </q-btn>
                <q-btn :dense="dense" flat icon="mdi-arrow-split-vertical" :color="split ? 'positive' : undefined" @click="split = !split" >
                    <q-tooltip>Split View</q-tooltip>
                </q-btn>
                <!-- <q-btn flat icon="mdi-fullscreen" @click="fullscreen=!fullscreen" >
                    <q-tooltip>Fullscreen</q-tooltip>
                </q-btn> -->
                <q-btn :dense="dense" flat icon="mdi-arrow-vertical-lock" :color="scrlck ? 'positive' : undefined" @click="scrlck = !scrlck">
                    <q-tooltip>Scroll Lock</q-tooltip>
                </q-btn>
            </q-toolbar>

            <q-separator />

            <q-item style="width: 100%;" v-if="style" :class="{'bg-grey-2': !$q.dark.isActive}">
                <q-item-section>
                    <q-input
                        dense
                        filled
                        outlined
                        :model-value="outputClass"
                        @update:model-value="$emit('update:output-class', $event)"
                        label="Class"
                    />
                </q-item-section>
                <q-item-section>
                    <q-input
                        dense
                        filled
                        outlined
                        autogrow
                        :model-value="outputStyle"
                        @update:model-value="$emit('update:output-style', $event)"
                        label="Style"
                    />
                </q-item-section>
            </q-item>

            <q-separator />

            <q-card-section class="q-pa-none" style="min-height: 400px;">
                <div style="display: flex;align-items: column;position: relative;">
                    <div class="row" style="max-height: 400px;min-height: 400px;flex:1;overflow-y:hidden;">
                        <textarea 
                            v-model="contentBody"
                            ref="textEditor"
                            :class="{
                                'editor-text': true, 
                                'col-6': split, 
                                'col-12': !split,
                                'bg-dark text-white': $q.dark.isActive,
                            }"
                        />
                        <div 
                            ref="outputWrapper"
                            :class="{ 'output-wrapper q-pa-sm': 1, 'col-6': split }"
                            :contenteditable="false"
                            @input="updateOutput"
                        >
                            <markdown
                                breaks
                                linkify
                                tasklists
                                :style="outputStyle"
                                :source="modelValue"
                                ref="outputContainer"
                                id="output-container"
                                :class="`editor-output ${outputClass}`"
                            />
                        </div>
                    </div>
                </div>
            </q-card-section>
        </q-card>
    </div>
</template>

<script>
import Markdown from 'vue3-markdown-it';

export default {
    name: 'MarkdownEditor',
    props: {
        outputOnly: {
            type: Boolean,
            default: false,
        },
        modelValue: String,
        outputClass: String,
        outputStyle: String,
        dense: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        Markdown,
    },
    data() {
        return {
            font: 'mobile',
            style: false,
            split: false,
            scrlck: true,
            fullscreen: false,
            textEditor: undefined,
            outputContainer: undefined,
            // markdownitInstance: Markdown,
            position: {
                start: 0,
                end: 0,
                row: 0,
                direction: 'none',
            },
            positionInterval: undefined,
        };
    },
    computed: {
        contentBody: {
            get() {
                return this.modelValue;
            },
            set(val) {
                this.$emit('update:modelValue', val);
            },
        },
    },
    methods: {
        updateOutput(event) {
            // console.log('updateOutput', event);
            console.warn('value', event.target.innerText);
        },
        cursorPos() {
            // TODO: Implement cursor row checks
            // - Detect what markdown syntax is being used where cursor is
            var ta = this.$refs.outputWrapper;
            var row =
                ta.innerText.substr(0, ta.selectionStart).split('\n').length - 1;
            var start = ta.selectionStart;
            var end = ta.selectionEnd;
            var direction = 'none';

            if (start > end) {
                direction = 'up';
            } else if (start < end) {
                direction = 'down';
            }

            this.position = {
                start: start,
                end: end,
                row: row,
                direction: direction,
            };

            // console.log(this.position);

            this.$emit('cursor-position', this.position);
        },
        prependText(text) {
            var ta = this.textEditor;
            var rows = ta.value.split('\n');
            var row =
                ta.value.substr(0, ta.selectionStart).split('\n').length - 1;
            var start = ta.selectionStart;
            var reset = false;

            text +=
                !rows[row].substr(0, 1).includes(' ') &&
                rows[row].substr(0, text.length) != text
                    ? ' '
                    : '';

            if (text == '#' && rows[row].substr(0, 6) == '######') {
                rows[row] = rows[row].replace('###### ', '');
                reset = true;
            } else {
                rows[row] = text + rows[row];
            }

            ta.value = rows.join('\n');
            ta.selectionStart = ta.selectionEnd = reset
                ? start - 7
                : start + text.length;
            ta.focus();
            this.$emit('update:modelValue', ta.value);
        },
        wrapText(open, close, beginning = false) {
            var ta = this.textEditor;
            var end = ta.selectionEnd;
            var start = ta.selectionStart;
            var selected = ta.value.substring(start, end);
            var replace = `${open}${selected}${close}`;
            ta.value =
                ta.value.substring(0, start) +
                replace +
                ta.value.substring(end, ta.value.length);
            // console.log(start + open.length, end + close.length);
            if (beginning)
                ta.setSelectionRange(start + open.length, end + open.length);
            else ta.setSelectionRange(start + open.length, end + close.length);
            ta.focus();
            this.$emit('update:modelValue', ta.value);
        },
        openInTab(url) {
            window.open(url, '_blank');
        },
        handleScrollLock(event = null) {
            if (!this.outputContainer) return;

            var percent =
                this.textEditor.scrollTop /
                (this.textEditor.scrollHeight - this.textEditor.clientHeight);

            if (this.scrlck == true) {
                this.$refs.outputContainer.scrollTop =
                    percent *
                    (this.$refs.outputContainer.scrollHeight -
                        this.$refs.outputContainer.clientHeight);
                // console.log(this.$refs.outputContainer.scrollTop);
            } else {
                console.error(this.$refs.outputContainer.scrollTop);
            }
        },
    },
    mounted() {
        this.outputContainer = document.getElementById('output-container');

        if (this.outputOnly) return;
        
        this.textEditor = this.$refs.textEditor;
        this.textEditor.addEventListener('scroll', this.handleScrollLock);
        
        // this.positionInterval = setInterval(this.cursorPos, 100);
    },
    unmounted() {

        if (this.outputOnly) return;

        this.textEditor.removeEventListener('scroll', this.handleScrollLock);

        clearInterval(this.positionInterval);
    },
    watch: {
        split(to) {
            if (to) {
                setTimeout(this.handleScrollLock, 150);
            }
        },
        // position: {
        //     handler(to, from) {
        //         // if (to.start != from.start || to.end != from.end) // console.log('position', to);
        //     },
        //     deep: true,
        // }
    },
};
</script>

<style lang="scss">
[class*='col-'] {
    float: left;
}

.editor {
    //height: 450px;

    &.fullscreen {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        height: 100%;
        margin-top: 0;
        position: fixed;

        .editor-header {
            border-radius: 0;
        }
    }

    .output-wrapper {
        height: 100%;
        overflow: hidden;
        overflow-y: auto;
        position: relative;
        transition: width 0.1s ease-in;
        border-left: 1px solid rgba(0, 0, 0, 0.12);

        &:not(.col-6) {
            display: none;
        }

        .editor-output {
            height: 100%;
        }
    }

    .editor-text {
        border: 0;
        outline: 0;
        height: 100%;
        resize: none;
        font: inherit;
        padding: 10px;

        &.full {
            width: 100%;
            border-right: 0;
        }
    }
}

.body--dark {
    .output-wrapper {
        border-color: grey;
    }

    .editor-text {
        background: transparent;
        color: #fff;
    }
}
</style>